import { useState } from 'react';

import { CaseDetail } from './CaseDetail';
import { CaseList } from './CaseList';
import { RequestDetail } from '../requests/RequestDetail';
import styles from './CaseScreen.module.css';

/**
 * The cases screen (M-06, task 5) — `/cases`, reachable only for a session
 * holding `case.view` (`App.tsx`'s own `rendersCases`, `navigation.ts`'s own
 * `cases` entry) — UNLIKE `/devices`/`/dispensaries`, `CasePolicy::view()`
 * requires the permission for BOTH `GET /cases` and `GET /cases/{case}`
 * (`cases.ts`'s own docblock), so this is the `/dispensary-codes` shape, not
 * the `/devices` one: a session without the permission never reaches this
 * component at all, and `permissions` travels down only so the LIST and
 * DETAIL can each decide what to render besides the record — `case.create`
 * gates the "New case" control, `case.update`/`case.close` each gate their
 * own control on the detail, the identical split `DeviceScreen.tsx`
 * establishes for `device.manage`.
 *
 * ## The list is never unmounted to show a detail
 *
 * The identical shape `DeviceScreen.tsx`'s own docblock explains at length:
 * an early return that swaps `<CaseList>` for `<CaseDetail>` destroys the
 * list's own `draftFilters`/`appliedFilters`/`cursor` state the moment a row
 * opens, so `onBack` would come back to a freshly-mounted, unfiltered list.
 * The list stays mounted in a `hidden` div; opening a row only adds
 * `<CaseDetail>` as a sibling.
 *
 * ## No session cache of a just-created case's claimed codes (M-06 fix
 * round, gap 2)
 *
 * `GET /cases/{case}` now reports `code_claims` on `CaseResource` itself —
 * `CaseDetail` fetches it directly for whichever case is opened, so this
 * screen no longer needs to remember the one case it just created in order
 * to show its codes; every case's detail works the same way.
 */

export interface CaseScreenProps {
  permissions: readonly string[];
  /**
   * A case to open immediately, from `#/cases/{uuid}`.
   *
   * This is what lets anything OUTSIDE the register open a case — the
   * dashboard's recent list, a link somebody pastes into a chat — without
   * that caller needing to know how this screen holds its selection.
   */
  initialCaseUuid?: string;
}

export function CaseScreen({ permissions, initialCaseUuid }: CaseScreenProps) {
  const [selected, setSelected] = useState<string | null>(initialCaseUuid ?? null);
  /**
   * A request opened FROM the case detail. Its own level of selection, not
   * a mode of `selected`: going Back from a request must return to the case
   * it was opened from, which means the case's own selection has to survive
   * underneath it.
   */
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);

  return (
    <div className={styles['page']}>
      {selectedRequest === null ? null : (
        <RequestDetail
          uuid={selectedRequest}
          permissions={permissions}
          onBack={() => {
            setSelectedRequest(null);
          }}
        />
      )}

      {selected === null ? null : (
        // `hidden` rather than unmounted, for the reason this file's own
        // docblock gives about the list: unmounting `CaseDetail` to show a
        // request would throw away its loaded record and its panels, so
        // coming back would re-fetch the whole case.
        <div hidden={selectedRequest !== null}>
          <CaseDetail
            uuid={selected}
            permissions={permissions}
            onBack={() => {
              setSelected(null);
            }}
            onOpenRequest={(requestUuid) => {
              setSelectedRequest(requestUuid);
            }}
          />
        </div>
      )}

      {/* `hidden`, never a ternary that unmounts — see this file's own
          docblock. */}
      <div hidden={selected !== null || selectedRequest !== null}>
        <CaseList
          permissions={permissions}
          active={selected === null && selectedRequest === null}
          onOpen={(uuid) => {
            setSelected(uuid);
          }}
        />
      </div>
    </div>
  );
}
